import { getClientBrandColor, getContrastingTextColor } from "#/utils/ColorUtils";
import {
  defaultOnClusterClickHandler,
  MarkerClusterer,
  SuperClusterAlgorithm,
  type Cluster,
  type Marker as ClustererMarker,
  type onClusterClickHandler,
  type Renderer,
} from "@googlemaps/markerclusterer";
import { useEffect, useRef } from "react";
import {
  CLUSTER_MAX_ZOOM,
  CLUSTER_RADIUS_PX,
  COINCIDENT_SPAN_DEGREES,
  MARKER_ICON_SIZE,
} from "./mapConstants";
import { getClusterIconSize, getClusterIconSvg, getMarkerIconSvg } from "./mapIcons";
import { getSpiderfyOffsets, offsetLatLngByPixels } from "./spiderfy";
import type { ResolvedMapMarker } from "./types";

export interface MarkerSelection {
  marker: ResolvedMapMarker;
  /** Where the popup should be anchored — differs from the marker's own
   * coordinates while it is fanned out by spiderfy. */
  position: google.maps.LatLngLiteral;
}

interface UseMarkerClustererParams {
  map: google.maps.Map | null;
  markers: ResolvedMapMarker[];
  /** Stable fingerprint of `markers`; the layer is rebuilt only when it changes. */
  signature: string;
  enabled: boolean;
  onSelect: (selection: MarkerSelection) => void;
}

const pinIcon = (color: string): google.maps.Icon => ({
  url: getMarkerIconSvg(color),
  scaledSize: new google.maps.Size(MARKER_ICON_SIZE, MARKER_ICON_SIZE),
  anchor: new google.maps.Point(MARKER_ICON_SIZE / 2, MARKER_ICON_SIZE),
});

/**
 * Zooming in only ever splits a cluster whose points are actually apart. Points
 * sharing (near enough) the same coordinates, or a cluster already at the
 * maximum clustering zoom, have to be fanned out instead.
 */
const shouldSpiderfy = (cluster: Cluster, map: google.maps.Map): boolean => {
  const zoom = map.getZoom();
  if (zoom !== undefined && zoom >= CLUSTER_MAX_ZOOM) return true;

  const bounds = cluster.bounds;
  if (!bounds) return true;

  const northEast = bounds.getNorthEast();
  const southWest = bounds.getSouthWest();

  return (
    Math.abs(northEast.lat() - southWest.lat()) < COINCIDENT_SPAN_DEGREES &&
    Math.abs(northEast.lng() - southWest.lng()) < COINCIDENT_SPAN_DEGREES
  );
};

const createRenderer = (brandColor: string): Renderer => {
  // Brand colours range from near-black to pale yellow, so the count is drawn
  // in whichever of black/white stays readable on top of the bubble.
  const labelColor = getContrastingTextColor(brandColor);

  return {
    render: ({ count, position }) => {
      const size = getClusterIconSize(count);

      return new google.maps.Marker({
        position,
        icon: {
          url: getClusterIconSvg(brandColor),
          scaledSize: new google.maps.Size(size, size),
          anchor: new google.maps.Point(size / 2, size / 2),
          labelOrigin: new google.maps.Point(size / 2, size / 2),
        },
        label: {
          text: String(count),
          color: labelColor,
          fontSize: "12px",
          fontWeight: "700",
        },
        // Keep bubbles above the individual pins they stand in for.
        zIndex: 1000 + count,
      });
    },
  };
};

/**
 * Groups markers into brand-coloured cluster bubbles that split apart as the
 * user zooms in, and fans out clusters that can never split because their
 * points share the same coordinates.
 */
export const useMarkerClusterer = ({
  map,
  markers,
  signature,
  enabled,
  onSelect,
}: UseMarkerClustererParams) => {
  // Markers and the callback are re-created on every parent render, so they are
  // read through refs — only `signature` may rebuild the layer.
  const markersRef = useRef(markers);
  markersRef.current = markers;

  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  const spiderPinsRef = useRef<google.maps.Marker[]>([]);
  const spiderLegsRef = useRef<google.maps.Polyline[]>([]);

  useEffect(() => {
    if (!map || !enabled) return;

    const brandColor = getClientBrandColor();
    const indexByMarker = new Map<ClustererMarker, number>();

    const clearSpiderfy = () => {
      spiderPinsRef.current.forEach((pin) => {
        google.maps.event.clearInstanceListeners(pin);
        pin.setMap(null);
      });
      spiderLegsRef.current.forEach((leg) => leg.setMap(null));
      spiderPinsRef.current = [];
      spiderLegsRef.current = [];
    };

    const spiderfy = (cluster: Cluster) => {
      const clusterMarkers = cluster.markers ?? [];
      const offsets = getSpiderfyOffsets(clusterMarkers.length);
      const origin = cluster.position;

      clusterMarkers.forEach((clusterMarker, index) => {
        const dataIndex = indexByMarker.get(clusterMarker);
        if (dataIndex === undefined) return;

        const data = markersRef.current[dataIndex];
        if (!data) return;

        const position = offsetLatLngByPixels(map, origin, offsets[index]);
        if (!position) return;

        const leg = new google.maps.Polyline({
          map,
          path: [origin, position],
          strokeColor: brandColor,
          strokeOpacity: 0.7,
          strokeWeight: 1.5,
          zIndex: 1,
        });

        const pin = new google.maps.Marker({
          map,
          position,
          icon: pinIcon(data.color),
          zIndex: 2000 + index,
        });

        pin.addListener("click", () => {
          onSelectRef.current({
            marker: markersRef.current[dataIndex],
            position: { lat: position.lat(), lng: position.lng() },
          });
        });

        spiderLegsRef.current.push(leg);
        spiderPinsRef.current.push(pin);
      });
    };

    const googleMarkers = markersRef.current.map((marker, index) => {
      const googleMarker = new google.maps.Marker({
        position: { lat: marker.latitude, lng: marker.longitude },
        icon: pinIcon(marker.color),
      });

      googleMarker.addListener("click", () => {
        clearSpiderfy();
        onSelectRef.current({
          marker: markersRef.current[index],
          position: { lat: marker.latitude, lng: marker.longitude },
        });
      });

      indexByMarker.set(googleMarker, index);
      return googleMarker;
    });

    const handleClusterClick: onClusterClickHandler = (event, cluster, clickedMap) => {
      clearSpiderfy();

      if (shouldSpiderfy(cluster, clickedMap)) {
        spiderfy(cluster);
        return;
      }

      defaultOnClusterClickHandler(event, cluster, clickedMap);
    };

    const clusterer = new MarkerClusterer({
      map,
      markers: googleMarkers,
      algorithm: new SuperClusterAlgorithm({
        maxZoom: CLUSTER_MAX_ZOOM,
        radius: CLUSTER_RADIUS_PX,
      }),
      renderer: createRenderer(brandColor),
      onClusterClick: handleClusterClick,
    });

    // Fanned-out pins are positioned in screen pixels, so they are only valid
    // at the zoom level they were built for.
    const mapListeners = [
      map.addListener("zoom_changed", clearSpiderfy),
      map.addListener("click", clearSpiderfy),
    ];

    return () => {
      mapListeners.forEach((listener) => listener.remove());
      clearSpiderfy();
      clusterer.clearMarkers();
      clusterer.setMap(null);
      googleMarkers.forEach((googleMarker) => {
        google.maps.event.clearInstanceListeners(googleMarker);
        googleMarker.setMap(null);
      });
    };
  }, [map, enabled, signature]);
};
